import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import { useIonViewDidEnter, useIonRouter } from '@ionic/react';
import {
  ActionGuardContainerProvider,
  ChildActionGuard,
} from '@/contexts/ActionGuardContainerContext';
import { useAppToast } from '@/hooks/useAppToast';
import { HelpText, UnlockFlow } from '@/hooks/useActionGuard';
import type { ExtraRequiredClaimUnlock } from '@/hooks/actionGuardConstraints';

import { StorageService } from '@/services/storage';
import { useActionGuardContainer } from '@/hooks/useActionGuardContainer';
import { useCasbin } from '@/contexts/CasbinContext';
import { RETURN_BUTTON_LABEL_PARAM } from '@/modules/profile/logic';
import { CLAIMS_LEVEL, type ClaimsLevel } from '@/modules/idv';
import PhoneVerificationModal from '../modules/idv/PhoneVerificationModal';
import EmailVerificationModal from '../modules/idv/EmailVerificationModal';

// Applied to every toast this container presents so E2E (and any other
// consumer) can target it the same way the old declarative <IonToast> exposed
// its data-testid.
const TOAST_HTML_ATTRIBUTES = {
  'data-testid': 'action-guard-container-toast',
};
const TOAST_DURATION = 5000;
// Fallback label for a consolation toast's navigating button when the message
// carries no [bracketed] label of its own. A route always implies a button.
const DEFAULT_CONSOLATION_BUTTON_TEXT = 'Go';

// The query string that asks the Profile Wizard's Attribution step for a
// button back to the action being unlocked, labelled `returnFromUnlockText`.
const returnSearch = (returnFromUnlockText?: string) =>
  returnFromUnlockText
    ? `?${RETURN_BUTTON_LABEL_PARAM}=${encodeURIComponent(returnFromUnlockText)}`
    : '';

export interface ActionGuardContainerProps {
  children: React.ReactNode;
  toastConfig?: {
    helpText: HelpText; // Supports [bracketed] action text in any of the help text strings
    frequency?: 'always' | 'daily' | 'weekly' | 'monthly';
    position?: 'top' | 'bottom' | 'middle';
  };
  groupIndicatorConfig?: {
    anyActionElem?: React.ReactElement;
    allActionsElem?: React.ReactElement;
    noActionsElem?: React.ReactElement;
  };
  onSuccess?: () => void;
}

/**
 * Check if reminder should be shown based on frequency
 */
async function shouldShowToasterReminder(
  frequency: 'always' | 'daily' | 'weekly' | 'monthly',
  toasterId: string
): Promise<boolean> {
  if (frequency === 'always') return true;

  const lastShown = await StorageService.getLastReminderTimestamp(toasterId);
  if (!lastShown) return true;

  const now = Date.now();
  const timeDiff = now - lastShown;

  switch (frequency) {
    case 'daily':
      return timeDiff >= 24 * 60 * 60 * 1000; // 1 day
    case 'weekly':
      return timeDiff >= 7 * 24 * 60 * 60 * 1000; // 7 days
    case 'monthly':
      return timeDiff >= 30 * 24 * 60 * 60 * 1000; // 30 days
    default:
      return true;
  }
}

/**
 * Combine verification summaries from all children to determine the combined verification needs
 * Logic:
 * - If any child needs PHONE_AND_EMAIL, result is PHONE_AND_EMAIL
 * - If we have both PHONE and EMAIL requirements, result is PHONE_AND_EMAIL
 * - If we have PHONE_OR_EMAIL with either PHONE or EMAIL, result is PHONE_OR_EMAIL
 * - If we have PHONE_OR_EMAIL alone, result is PHONE_OR_EMAIL
 * - If we have only PHONE, result is PHONE
 * - If we have only EMAIL, result is EMAIL
 * - If all are null, result is null
 */
function getClaimsThatWouldUnlockAllChildren(
  children: Map<string, ChildActionGuard>
): ClaimsLevel | null {
  if (children.size === 0) {
    return null;
  }

  const claims: (ClaimsLevel | null)[] = [];
  children.forEach(child => {
    claims.push(child.claimsThatWouldUnlock);
  });

  // Filter out null values for combination logic
  const nonNullClaims = claims.filter((v): v is ClaimsLevel => v !== null);

  // If all are null, return null
  if (nonNullClaims.length === 0) {
    return null;
  }

  // If any child needs PHONE_AND_EMAIL, result is PHONE_AND_EMAIL
  if (nonNullClaims.includes(CLAIMS_LEVEL.PHONE_AND_EMAIL)) {
    return CLAIMS_LEVEL.PHONE_AND_EMAIL;
  }

  // Check if we have both PHONE and EMAIL requirements
  const hasPhone = nonNullClaims.includes(CLAIMS_LEVEL.PHONE);
  const hasEmail = nonNullClaims.includes(CLAIMS_LEVEL.EMAIL);
  const hasPhoneOrEmail = nonNullClaims.includes(CLAIMS_LEVEL.PHONE_OR_EMAIL);

  // If we have both PHONE and EMAIL, result is PHONE_AND_EMAIL
  if (hasPhone && hasEmail) {
    return CLAIMS_LEVEL.PHONE_AND_EMAIL;
  }

  // If we have PHONE_OR_EMAIL with either PHONE or EMAIL, result is PHONE_OR_EMAIL
  if (hasPhoneOrEmail && (hasPhone || hasEmail)) {
    return CLAIMS_LEVEL.PHONE_OR_EMAIL;
  }

  // If we have PHONE_OR_EMAIL alone, result is PHONE_OR_EMAIL
  if (hasPhoneOrEmail) {
    return CLAIMS_LEVEL.PHONE_OR_EMAIL;
  }

  // If we have only PHONE, result is PHONE
  if (hasPhone) {
    return CLAIMS_LEVEL.PHONE;
  }

  // If we have only EMAIL, result is EMAIL
  if (hasEmail) {
    return CLAIMS_LEVEL.EMAIL;
  }

  // Fallback (shouldn't happen, but return null for safety)
  return null;
}

/**
 * The union of unmet client-side extra claims across all children — the parallel
 * axis to the verification `ClaimsLevel` merge above. Deduped by claim key.
 */
function getExtraClaimsUnion(
  children: Map<string, ChildActionGuard>
): ExtraRequiredClaimUnlock[] {
  const byKey = new Map<string, ExtraRequiredClaimUnlock>();
  children.forEach(child => {
    const extra = child.extraClaimUnlock;
    if (extra && !byKey.has(extra.key)) {
      byKey.set(extra.key, extra);
    }
  });
  return Array.from(byKey.values());
}

/**
 * ActionGuardContainer component that wraps multiple ActionGuard components
 * and shows a single toast when ALL actions are locked but some are unlockable
 */
export const ActionGuardContainer: React.FC<ActionGuardContainerProps> = ({
  children,
  toastConfig,
  groupIndicatorConfig,
  onSuccess,
}) => {
  const router = useIonRouter();
  const { refreshPermissions: refreshCasbinPermissions } = useCasbin();
  const {
    calculateHelpText,
    calculateUnlockFlow,
    extractBracketedText,
    stripBracketedText,
  } = useActionGuardContainer();
  const [presentToast] = useAppToast();
  const [childrenMap, setChildrenMap] = useState<Map<string, ChildActionGuard>>(
    new Map()
  );
  const [isPhoneModalOpen, setIsPhoneModalOpen] = useState(false);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const hasShownToastRef = useRef(false);
  const toasterIdRef = useRef<string>(
    `toaster_${router.routeInfo?.pathname || 'unknown'}_${Date.now()}`
  );
  // A display:none probe used to locate this container's enclosing Ionic page so
  // the auto-toast can be suppressed while that page is not the visible view.
  const pageProbeRef = useRef<HTMLSpanElement>(null);

  // True when this container's page is stacked behind another view. Ionic marks
  // inactive pages `ion-page-hidden`; a toast is presented at the app root, so a
  // backgrounded container (e.g. an action page still mounted under the Profile
  // Wizard) would otherwise pop a toast over an unrelated screen when its
  // children re-evaluate (e.g. the user edits their names in the wizard).
  const isPageHidden = useCallback(
    () => pageProbeRef.current?.closest('.ion-page-hidden') != null,
    []
  );

  // Register/unregister children
  const registerChild = useCallback((child: ChildActionGuard) => {
    setChildrenMap(prev => {
      const next = new Map(prev);
      next.set(child.id, child);
      return next;
    });
  }, []);

  const unregisterChild = useCallback((id: string) => {
    setChildrenMap(prev => {
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }, []);

  // Navigation function to unlock action - use container's own modals when possible
  const navigateToUnlock = useCallback(
    (targetUnlockFlow: UnlockFlow, returnFromUnlockText?: string) => {
      const currentPath =
        router.routeInfo?.pathname || location.pathname || '/';

      switch (targetUnlockFlow) {
        case UnlockFlow.EmailVerificationModal:
          setIsEmailModalOpen(true);
          return;
        case UnlockFlow.PhoneVerificationModal:
          setIsPhoneModalOpen(true);
          return;
        case UnlockFlow.ProfileWizardStart:
          // The return button rides through the wizard's steps to Attribution
          // (see ProfileWizardContext.navigateWithOrigin).
          router.push(
            `/profile${returnSearch(returnFromUnlockText)}`,
            'forward',
            'push',
            {
              from: currentPath,
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } as any
          );
          return;
        case UnlockFlow.ProfileWizardPhone:
          router.push('/profile/steps/phone', 'forward', 'push', {
            from: currentPath,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any);
          return;
        case UnlockFlow.ProfileWizardEmail:
          router.push('/profile/steps/email', 'forward', 'push', {
            from: currentPath,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any);
          return;
        case UnlockFlow.ProfileWizardAttribution: {
          // The Attribution step reads `returnButtonLabel` from the query string and
          // renders a footer button that returns to the action being unlocked via
          // router.goBack() — a plain 'push' here leaves that page directly below
          // in history, and goBack is the only reliable way back out of the
          // profile wizard's nested router outlet to a sibling route.
          router.push(
            `/profile/steps/attribution${returnSearch(returnFromUnlockText)}`,
            'forward',
            'push'
          );
          return;
        }
        default:
          throw new Error(`Invalid unlock flow: ${targetUnlockFlow}`);
      }
    },
    [router]
  );

  // After verification modals succeed, refresh casbin permissions so
  // ActionGuard children re-evaluate immediately with the new JWT.
  const handleVerificationSuccess = useCallback(async () => {
    await refreshCasbinPermissions();
    onSuccess?.();
  }, [refreshCasbinPermissions, onSuccess]);

  const triggerToast = useCallback(
    (
      targetUnlockFlow: UnlockFlow,
      message: string,
      returnFromUnlockText?: string,
      onUnlock?: (unlockFlow: UnlockFlow) => void | Promise<void>
    ) => {
      const buttonText = extractBracketedText(message);
      presentToast({
        message: stripBracketedText(message),
        buttons: buttonText
          ? [
              {
                text: buttonText,
                handler: async () => {
                  if (onUnlock) await onUnlock(targetUnlockFlow);
                  navigateToUnlock(targetUnlockFlow, returnFromUnlockText);
                },
              },
            ]
          : undefined,
        duration: TOAST_DURATION,
        htmlAttributes: TOAST_HTML_ATTRIBUTES,
      });
    },
    [presentToast, extractBracketedText, stripBracketedText, navigateToUnlock]
  );

  // Present a consolation toast for a lock the user cannot self-clear. With no
  // `route` the toast is buttonless; with one, it navigates to that in-app route,
  // tagging the current page as a `from` query param so the destination can
  // return here. The button's label is the message's [bracketed] text, or "Go"
  // when absent (a route always implies a button). Unlike `triggerToast`, the
  // target isn't an UnlockFlow — it points the user at more info / customer
  // service.
  const triggerConsolationToast = useCallback(
    (message: string, route?: string) => {
      const currentPath =
        router.routeInfo?.pathname || location.pathname || '/';
      presentToast({
        message: stripBracketedText(message),
        buttons: route
          ? [
              {
                text:
                  extractBracketedText(message) ??
                  DEFAULT_CONSOLATION_BUTTON_TEXT,
                handler: () => {
                  const separator = route.includes('?') ? '&' : '?';
                  router.push(
                    `${route}${separator}from=${encodeURIComponent(currentPath)}`,
                    'forward',
                    'push'
                  );
                },
              },
            ]
          : undefined,
        duration: TOAST_DURATION,
        htmlAttributes: TOAST_HTML_ATTRIBUTES,
      });
    },
    [presentToast, extractBracketedText, stripBracketedText, router]
  );

  // Context value
  const contextValue = useMemo(
    () => ({
      registerChild,
      unregisterChild,
      navigateToUnlock,
      triggerToast,
      triggerConsolationToast,
    }),
    [
      registerChild,
      unregisterChild,
      navigateToUnlock,
      triggerToast,
      triggerConsolationToast,
    ]
  );

  // Aggregate status from all children
  const allLocked = useMemo(() => {
    if (childrenMap.size === 0) return false;
    return Array.from(childrenMap.values()).every(
      child => !child.canPerformAction
    );
  }, [childrenMap]);

  const anyUnlockable = useMemo(() => {
    return Array.from(childrenMap.values()).some(child => child.isUnlockable);
  }, [childrenMap]);

  // While any child's permissions are still settling, its transient
  // (canPerformAction=false, isUnlockable=true) state would read as a lock. Hold
  // the auto-toast until every child has resolved so we don't flash a spurious
  // "set up your profile" nudge on a page the user can actually use.
  const anyChildLoading = useMemo(() => {
    return Array.from(childrenMap.values()).some(child => child.isLoading);
  }, [childrenMap]);

  const hasAnyEnabledAction = useMemo(() => {
    return Array.from(childrenMap.values()).some(
      child => child.canPerformAction
    );
  }, [childrenMap]);

  const hasAllEnabledActions = useMemo(() => {
    if (childrenMap.size === 0) {
      return false;
    }

    return Array.from(childrenMap.values()).every(
      child => child.canPerformAction
    );
  }, [childrenMap]);

  // Combined unlock needs across children, merged on two parallel axes: the
  // verification `ClaimsLevel` (existing lattice) and the client-side extra
  // claims. The auto-toast is directed at whatever serves the union.
  const claimsThatWouldUnlockAllChildren = useMemo(() => {
    return getClaimsThatWouldUnlockAllChildren(childrenMap);
  }, [childrenMap]);

  const extraClaimsUnion = useMemo(() => {
    return getExtraClaimsUnion(childrenMap);
  }, [childrenMap]);

  // What the auto-toast should say and where its button goes.
  const autoUnlock = useMemo(() => {
    const baseHelp = toastConfig?.helpText;

    // No extra claims → pure verification path (unchanged behavior): direct to the
    // flow that satisfies the merged ClaimsLevel.
    if (extraClaimsUnion.length === 0) {
      const help = baseHelp
        ? calculateHelpText(claimsThatWouldUnlockAllChildren, baseHelp)
        : '';
      return {
        message: help ? stripBracketedText(help) : '',
        buttonText: help ? extractBracketedText(help) : null,
        unlockFlow: calculateUnlockFlow(claimsThatWouldUnlockAllChildren),
        returnFromUnlockText: undefined as string | undefined,
      };
    }

    // Both axes needed (some child needs verification AND some needs an extra
    // claim) → send to the wizard start, which walks every step and thus serves
    // the whole union.
    if (claimsThatWouldUnlockAllChildren !== null) {
      const help = baseHelp ? calculateHelpText(null, baseHelp) : '';
      return {
        message: help ? stripBracketedText(help) : '',
        buttonText: help ? extractBracketedText(help) : null,
        unlockFlow: UnlockFlow.ProfileWizardStart,
        returnFromUnlockText: extraClaimsUnion[0].returnFromUnlockText,
      };
    }

    // Extra claims only → use the (first) extra claim's own unlock descriptor.
    const extra = extraClaimsUnion[0];
    return {
      message: stripBracketedText(extra.helpText),
      buttonText: extractBracketedText(extra.helpText),
      unlockFlow: extra.unlockFlow,
      returnFromUnlockText: extra.returnFromUnlockText,
    };
  }, [
    extraClaimsUnion,
    claimsThatWouldUnlockAllChildren,
    toastConfig?.helpText,
    calculateHelpText,
    calculateUnlockFlow,
    extractBracketedText,
    stripBracketedText,
  ]);

  const { message: toastMessage, buttonText } = autoUnlock;

  // Show toast if all children are locked and some are unlockable
  // Check both on view enter and when conditions change
  const checkAndShowToast = useCallback(async () => {
    if (
      toastConfig &&
      allLocked &&
      anyUnlockable &&
      !anyChildLoading &&
      !isPageHidden() &&
      !hasShownToastRef.current
    ) {
      const shouldShow = await shouldShowToasterReminder(
        toastConfig.frequency || 'always',
        toasterIdRef.current
      );

      if (shouldShow) {
        presentToast({
          message: toastMessage,
          buttons: buttonText
            ? [
                {
                  text: buttonText,
                  handler: () =>
                    navigateToUnlock(
                      autoUnlock.unlockFlow,
                      autoUnlock.returnFromUnlockText
                    ),
                },
              ]
            : undefined,
          duration: TOAST_DURATION,
          position: toastConfig.position,
          htmlAttributes: TOAST_HTML_ATTRIBUTES,
        });
        hasShownToastRef.current = true;
        // Update timestamp
        await StorageService.setLastReminderTimestamp(
          toasterIdRef.current
        ).catch(error => {
          // eslint-disable-next-line no-console
          console.error('Failed to save reminder timestamp:', error);
        });
      }
    }
  }, [
    toastConfig,
    allLocked,
    anyUnlockable,
    anyChildLoading,
    isPageHidden,
    presentToast,
    toastMessage,
    buttonText,
    autoUnlock,
    navigateToUnlock,
  ]);

  useIonViewDidEnter(() => {
    checkAndShowToast();
  });

  // Also check when conditions change (e.g., after ActionGuards register)
  useEffect(() => {
    checkAndShowToast();
  }, [checkAndShowToast]);

  // Reset toast flag when at least one child becomes available
  useEffect(() => {
    if (!allLocked) {
      hasShownToastRef.current = false;
    }
  }, [allLocked]);

  const groupIndicatorElem = useMemo(() => {
    if (!groupIndicatorConfig || childrenMap.size === 0) {
      return null;
    }

    if (hasAllEnabledActions && groupIndicatorConfig.allActionsElem) {
      return groupIndicatorConfig.allActionsElem;
    }

    if (hasAnyEnabledAction) {
      return groupIndicatorConfig.anyActionElem ?? null;
    }

    return groupIndicatorConfig.noActionsElem ?? null;
  }, [
    childrenMap.size,
    groupIndicatorConfig,
    hasAllEnabledActions,
    hasAnyEnabledAction,
  ]);

  return (
    <ActionGuardContainerProvider value={contextValue}>
      <span ref={pageProbeRef} aria-hidden="true" style={{ display: 'none' }} />
      {children}
      {groupIndicatorElem}
      {/* Render modals for toaster's own use */}
      {anyUnlockable && (
        <>
          <PhoneVerificationModal
            isOpen={isPhoneModalOpen}
            onClose={_reason => setIsPhoneModalOpen(false)}
            continuePath={router.routeInfo?.pathname || '/'}
            onSuccess={handleVerificationSuccess}
          />
          <EmailVerificationModal
            isOpen={isEmailModalOpen}
            onClose={_reason => setIsEmailModalOpen(false)}
            continuePath={router.routeInfo?.pathname || '/'}
            onSuccess={handleVerificationSuccess}
          />
        </>
      )}
    </ActionGuardContainerProvider>
  );
};
